import type { ILog } from '@monofe/util';
import { injectLog } from '@monofe/util';
import type {
    ForwardRefExoticComponent,
    MutableRefObject,
    PropsWithoutRef,
    RefAttributes,
    RefObject,
} from 'react';
import { forwardRef } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import type {
    IZoomPanPinch,
    IZoomPanPinchRef as IZoomPanPinchReference,
} from './ZoomPanPinch.interface';
import styles from './ZoomPanPinch.module.css';

type ForwardReferenceObjectType =
    | ((instance: IZoomPanPinchReference | null) => void)
    | MutableRefObject<IZoomPanPinchReference | null>
    | null;

/* We don't want to expose our dependency so cast between IZoomPanPinchRef to ReactZoomPanPinchRef when possible & error out when not */
function isInternalReference(
    reference: ForwardReferenceObjectType,
): reference is RefObject<ReactZoomPanPinchRef> {
    const referenceCast = (reference as MutableRefObject<ReactZoomPanPinchRef>).current;
    return reference === null || referenceCast === null || referenceCast.zoomToElement !== null;
}

type ReturnType = ForwardRefExoticComponent<
    PropsWithoutRef<IZoomPanPinch> & RefAttributes<IZoomPanPinchReference>
>;

function _className(name: 'Wrapper' | 'Content'): string {
    const style = styles[name];
    if (style === undefined) throw new Error(`Couldn't find style class: ${name}`);
    return style;
}

function _ZoomPanPinch(logger?: ILog): ReturnType {
    return forwardRef<IZoomPanPinchReference, PropsWithoutRef<IZoomPanPinch>>(
        (properties, reference) => {
            const isValidReference = isInternalReference(reference);
            if (!isValidReference) {
                logger?.error(`Invalid ref for ZoomPanPinch: ${JSON.stringify(reference)}`);
                return null;
            }

            return (
                <TransformWrapper
                    ref={reference}
                    initialScale={properties.startZoom}
                    initialPositionX={properties.startX}
                    initialPositionY={properties.startY}
                    minScale={properties.minZoom}
                    maxScale={properties.maxZoom}
                    onZoom={properties.onZoom}
                >
                    <TransformComponent
                        wrapperClass={`${properties.className ?? ''} ${_className('Wrapper')}`}
                        wrapperStyle={properties.style}
                        contentClass={_className('Content')}
                    >
                        {properties.children}
                    </TransformComponent>
                </TransformWrapper>
            );
        },
    );
}

function ZoomPanPinch(): ReturnType {
    return _ZoomPanPinch(injectLog());
}

export default ZoomPanPinch;
